import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useSupportUsers,
  useSupportUserApi,
  usePageNotifications,
  useLogger,
  usePermissionsApi,
} from 'hooks'
import { SidePanelContent } from 'components'
import { EditSupportUser } from 'components/supportUsers'
import { Table } from 'cs-kevlar-react'
import { ColumnDef } from 'cs-kevlar-react/lib/es/table/base'
import {
  ApiPermissionsConfigurationViewModel,
  SupportUser,
  SupportUserPermission,
} from '../../api'
import { matchIdOrDefault, considerDataToBeStale } from 'utils'
import {
  SupportUserSpreadPermissions,
  permissionMapping,
  PermissionType,
} from 'hooks/useSupportUserApi'
import { Edit } from 'cs-kevlar-react/lib/es/icons'
import {
  FilterOptions,
  PaginationOptions,
} from 'cs-kevlar-react/lib/es/table/Table'
import { AsyncPayload } from 'components/base'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const PageTitle = 'Support Users'

type SupportUsersProps = {
  supportUserId?: string
}

type ValidMenuKey = 'Edit'

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableMultipleSearchTerms: true,
  enableSuggestions: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'user',
  pluralAdjective: 'users',
}

const autoGeneratedColumns = Object.keys(permissionMapping).map(
  (permission) => {
    const permissionType = permission as PermissionType

    return {
      label: permissionMapping[permissionType].label,
      sortable: true,
      sortByDefault: false,
      sortValueFunc: (row: SupportUserSpreadPermissions) => row[permissionType],
      filterable: true,
      dataFunc: (row: SupportUserSpreadPermissions) =>
        !row.isDisabled ? row[permissionType] : '',
    }
  },
)

const columns: ColumnDef<SupportUserSpreadPermissions, ValidMenuKey>[] = [
  {
    label: 'Support User',
    sortable: true,
    sortByDefault: true,
    sortValueFunc: (row: SupportUserSpreadPermissions) => row.username,
    filterable: true,
    dataProperty: 'username' as keyof SupportUserSpreadPermissions,
  },
  ...autoGeneratedColumns,
  {
    label: 'Disabled',
    sortable: true,
    sortByDefault: false,
    sortValueFunc: (row: SupportUserSpreadPermissions) => row.isDisabled,
    filterable: false,
    dataFunc: (row: SupportUserSpreadPermissions) =>
      row.isDisabled ? 'Disabled' : '',
  },
  {
    label: '',
    sortable: false,
    sortByDefault: false,
    filterable: false,
    menuGroups: [
      {
        options: [
          {
            label: 'Edit',
            key: 'Edit',
            icon: Edit,
          },
        ],
      },
    ],
  },
]

const defaultSupportUser: SupportUser = {
  id: 'NEW',
  username: '',
  isDisabled: false,
  permissions: [],
  lastLoggedInOn: '',
}

type MenuEls = {
  [key: string]: HTMLElement | null
}

type EditingSupportUserState = SupportUser | null

const buildNavigationUrl = (supportUser: EditingSupportUserState) => {
  if (supportUser === null) return `/supportUsers`

  return `/supportUsers/${supportUser.id}`
}

export const SupportUsers: React.FunctionComponent = () => {

  const params = useParams<SupportUsersProps>()
  if(!params) return null

  const navigate = useNavigate()
  const { logError } = useLogger()

  const {
    supportUsers,
    lastApiReceiveAt,
    supportUsersContext,
    dispatch: dispatchSupportUserAction,
    spreadPermissions,
  } = useSupportUsers()

  const { getSupportUsers } = useSupportUserApi()
  const { getPermissionsAndScopes } = usePermissionsApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  usePageViewAudit(PageTitle, true, true)

  const [menuEls, setMenuEls] = React.useState<MenuEls>({})
  const [permissionConfiguration, setPermissionConfiguration] = React.useState<
    AsyncPayload<ApiPermissionsConfigurationViewModel>
  >({
    payload: null,
    isLoading: true,
  })

  const handleMenuClose = (id: string) => {
    setMenuEls({
      ...menuEls,
      [id]: null,
    })
  }

  const selectSupportUserForEditing = (supportUser: SupportUser | null) => {
    navigate(buildNavigationUrl(supportUser))
  }

  // load the supportUser data
  React.useEffect(() => {
    getPermissionsAndScopes().then((response) => {
      if (!response) return

      if (response.status === 200) {
        setPermissionConfiguration({
          payload: response.data,
          isLoading: false,
        })
      }
    })

    // if the data is new enough then don't bother refetching
    if (!considerDataToBeStale(supportUsers, lastApiReceiveAt)) return

    getSupportUsers()
      .then((response) => {
        if (!response) return

        if (response.status === 200) {
          const supportUsers = response.data.data.map(spreadPermissions)

          dispatchSupportUserAction({
            type: 'LoadSupportUsers',
            payload: supportUsers,
            timestamp: Date.now(),
          })
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error loading the supportUser list',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error loading the support user list',
        })
      })
  }, [])

  const editingSupportUser = matchIdOrDefault(
    supportUsersContext.supportUsers,
    params.supportUserId,
    defaultSupportUser,
  )

  const isEditing = editingSupportUser !== null

  const handleMenuClick = (key: ValidMenuKey, row: SupportUser) => {
    handleMenuClose(row.id)

    if (key === 'Edit') {
      selectSupportUserForEditing(row)
      return
    }
  }

  return (
    <React.Fragment>
      <Table
        filterOptions={FILTER_OPTIONS}
        paginationOptions={PAGINATION_OPTIONS}
        data={supportUsers as SupportUserSpreadPermissions[]}
        columns={
          columns as ColumnDef<SupportUserSpreadPermissions, ValidMenuKey>[]
        }
        ariaLabel="Support Users"
        useDefaultRow
        handleMenuClick={handleMenuClick}
        uniqueIdentifier="SupportUsersList"
      />
      {isEditing && (
        <SidePanelContent
          anchor="right"
          title={`Edit Support User`}
          onClose={() => selectSupportUserForEditing(null)}
        >
          <EditSupportUser
            key={(editingSupportUser as SupportUser).id}
            supportUser={editingSupportUser as SupportUser}
            onClose={() => selectSupportUserForEditing(null)}
            permissionConfiguration={permissionConfiguration.payload}
          />
        </SidePanelContent>
      )}
    </React.Fragment>
  )
}

export const SupportUsersPage: PageDefinition = {
  routes: () => [
    '/supportUsers',
    '/supportUsers/:supportUserId',
  ],
  type: [PageType.Support],
  sortOrder: () => 3,
  component: <SupportUsers />,
  navLinkText: PageTitle,
  pageTitle: PageTitle,
  canAccess: (user) => {
    return (
      user !== null &&
      user.isSupport &&
      user.permissions &&
      (user.permissions.includes(SupportUserPermission.ViewSupportUsers) ||
        user.permissions.includes(SupportUserPermission.ManageSupportUsers))
    )
  },
  buildNavLink: (user) => {
    return buildNavigationUrl(null)
  },
}
